import { AnimatePresence, motion, type Variants } from 'framer-motion'
import { cn } from 'lib/utils'
import { memo } from 'react'
import type { TextAnimateProps } from './types'
import { staggerTimings } from './constants'
import { splitTextByType, createSegmentKey, buildFinalVariants } from './utils'

const TextAnimateBase = ({
  children,
  delay = 0,
  duration = 0.3,
  variants,
  className,
  segmentClassName,
  as: Component = 'p',
  startOnView = true,
  once = false,
  by = 'word',
  animation = 'fadeIn',
  ...props
}: TextAnimateProps) => {
  const MotionComponent = motion.create(Component)
  const segments = splitTextByType(children, by)
  const finalVariants = buildFinalVariants(variants, animation, delay, duration, segments.length)

  return (
    <AnimatePresence mode="popLayout">
      <MotionComponent
        variants={finalVariants.container as Variants}
        initial="hidden"
        whileInView={startOnView ? 'show' : undefined}
        animate={startOnView ? undefined : 'show'}
        exit="exit"
        className={cn('whitespace-pre-wrap', className)}
        viewport={{ once }}
        {...props}
      >
        {segments.map((segment, i) => {
          const segmentKey = createSegmentKey(segment, by)

          return (
            <motion.span
              key={segmentKey}
              variants={finalVariants.item}
              custom={i * staggerTimings[by]}
              className={cn(
                by === 'line' ? 'block' : 'inline-block whitespace-pre',
                by === 'character' && '',
                segmentClassName,
              )}
            >
              {segment}
            </motion.span>
          )
        })}
      </MotionComponent>
    </AnimatePresence>
  )
}

export const TextAnimate = memo(TextAnimateBase)
